import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Save, X, 
  ChevronDown, ChevronRight, AlertCircle, Check,
  Search, FolderTree, Box, Target, CheckSquare,
  ArrowUp, ArrowDown
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

const CompetencesManagerV2 = () => {
  // États
  const [poles, setPoles] = useState([]);
  const [blocs, setBlocs] = useState({});
  const [activites, setActivites] = useState({});
  const [taches, setTaches] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedFormation, setSelectedFormation] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [editingItem, setEditingItem] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  // États pour l'ajout
  const [showAddPole, setShowAddPole] = useState(false);
  const [showAddBloc, setShowAddBloc] = useState(null);
  const [showAddActivite, setShowAddActivite] = useState(null);
  const [showAddTache, setShowAddTache] = useState(null);

  const typesFormation = [
    { value: 'all', label: 'Toutes les formations' },
    { value: 'CAP_MVA', label: 'CAP Maintenance des Véhicules' },
    { value: 'BAC_PRO_MVA', label: 'BAC PRO Maintenance des Véhicules' },
    { value: 'CAP_CARROSSERIE', label: 'CAP Carrosserie' },
    { value: 'BAC_PRO_CARROSSERIE', label: 'BAC PRO Carrosserie' },
    { value: 'CAP_PEINTRE_1AN', label: 'CAP Peintre 1 an' },
    { value: 'CQP_MECANIQUE', label: 'CQP Mécanique' },
    { value: 'CQP_CARROSSERIE', label: 'CQP Carrosserie' }
  ];

  const [newPole, setNewPole] = useState({
    code: '',
    titre: '',
    description: '',
    type_formation: 'CAP_MVA',
    ordre: 0
  });

  const [newBloc, setNewBloc] = useState({
    code: '',
    titre: '',
    description: '',
    ordre: 0
  });

  const [newActivite, setNewActivite] = useState({
    code: '',
    titre: '',
    description: '',
    ordre: 0
  });

  const [newTache, setNewTache] = useState({
    code: '',
    titre: '',
    description: '',
    ordre: 0
  });

  // Charger les données
  useEffect(() => {
    loadAllData();
  }, [selectedFormation]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Charger les pôles
      let poleQuery = supabase.from('poles').select('*').order('ordre');
      if (selectedFormation !== 'all') {
        poleQuery = poleQuery.eq('type_formation', selectedFormation);
      }
      const { data: polesData, error: polesError } = await poleQuery;
      if (polesError) throw polesError;

      setPoles(polesData || []);

      if (polesData && polesData.length > 0) {
        const poleIds = polesData.map(p => p.id);

        // Charger les blocs
        const { data: blocsData, error: blocsError } = await supabase
          .from('blocs')
          .select('*')
          .in('pole_id', poleIds)
          .order('ordre');
        if (blocsError) throw blocsError;

        const blocsGrouped = (blocsData || []).reduce((acc, bloc) => {
          if (!acc[bloc.pole_id]) acc[bloc.pole_id] = [];
          acc[bloc.pole_id].push(bloc);
          return acc;
        }, {});
        setBlocs(blocsGrouped);

        if (blocsData && blocsData.length > 0) {
          const blocIds = blocsData.map(b => b.id);

          // Charger les activités
          const { data: activitesData, error: activitesError } = await supabase
            .from('activites')
            .select('*')
            .in('bloc_id', blocIds)
            .order('ordre');
          if (activitesError) throw activitesError;

          const activitesGrouped = (activitesData || []).reduce((acc, activite) => {
            if (!acc[activite.bloc_id]) acc[activite.bloc_id] = [];
            acc[activite.bloc_id].push(activite);
            return acc;
          }, {});
          setActivites(activitesGrouped);

          if (activitesData && activitesData.length > 0) {
            const activiteIds = activitesData.map(a => a.id);

            // Charger les tâches
            const { data: tachesData, error: tachesError } = await supabase
              .from('taches')
              .select('*')
              .in('activite_id', activiteIds)
              .order('ordre');
            if (tachesError) throw tachesError;

            const tachesGrouped = (tachesData || []).reduce((acc, tache) => {
              if (!acc[tache.activite_id]) acc[tache.activite_id] = [];
              acc[tache.activite_id].push(tache);
              return acc;
            }, {});
            setTaches(tachesGrouped);
          }
        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      showMessage('error', 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const toggleExpand = (type, id) => {
    const key = `${type}_${id}`;
    setExpandedItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // CRUD Pôle
  const handleAddPole = async () => {
    try {
      const { data, error } = await supabase
        .from('poles')
        .insert([newPole])
        .select()
        .single();

      if (error) throw error;

      setPoles([...poles, data]);
      setShowAddPole(false);
      setNewPole({ code: '', titre: '', description: '', type_formation: 'CAP_MVA', ordre: 0 });
      showMessage('success', 'Pôle ajouté avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de l\'ajout du pôle');
    }
  };

  const handleUpdatePole = async (id, updates) => {
    try {
      const { error } = await supabase
        .from('poles')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setPoles(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
      setEditingItem(null);
      showMessage('success', 'Pôle mis à jour');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de la mise à jour');
    }
  };

  const handleDeletePole = async (id) => {
    if (!window.confirm('Supprimer ce pôle et tous ses éléments enfants ?')) return;
    
    try {
      const { error } = await supabase.from('poles').delete().eq('id', id);
      if (error) throw error;

      setPoles(prev => prev.filter(p => p.id !== id));
      showMessage('success', 'Pôle supprimé');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de la suppression');
    }
  };

  // CRUD Bloc
  const handleAddBloc = async (poleId) => {
    try {
      const blocToAdd = { ...newBloc, pole_id: poleId };
      const { data, error } = await supabase
        .from('blocs')
        .insert([blocToAdd])
        .select()
        .single();

      if (error) throw error;

      setBlocs(prev => ({
        ...prev,
        [poleId]: [...(prev[poleId] || []), data]
      }));
      setShowAddBloc(null);
      setNewBloc({ code: '', titre: '', description: '', ordre: 0 });
      showMessage('success', 'Bloc ajouté avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de l\'ajout du bloc');
    }
  };

  const handleUpdateBloc = async (id, poleId, updates) => {
    try {
      const { error } = await supabase
        .from('blocs')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setBlocs(prev => ({
        ...prev,
        [poleId]: prev[poleId].map(b => b.id === id ? { ...b, ...updates } : b)
      }));
      setEditingItem(null);
      showMessage('success', 'Bloc mis à jour');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteBloc = async (id, poleId) => {
    if (!window.confirm('Supprimer ce bloc et tous ses éléments enfants ?')) return;
    
    try {
      const { error } = await supabase.from('blocs').delete().eq('id', id);
      if (error) throw error;

      setBlocs(prev => ({
        ...prev,
        [poleId]: prev[poleId].filter(b => b.id !== id)
      }));
      showMessage('success', 'Bloc supprimé');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de la suppression');
    }
  };

  // CRUD Activité
  const handleAddActivite = async (blocId) => {
    try {
      const activiteToAdd = { ...newActivite, bloc_id: blocId };
      const { data, error } = await supabase
        .from('activites')
        .insert([activiteToAdd])
        .select()
        .single();

      if (error) throw error;

      setActivites(prev => ({
        ...prev,
        [blocId]: [...(prev[blocId] || []), data]
      }));
      setShowAddActivite(null);
      setNewActivite({ code: '', titre: '', description: '', ordre: 0 });
      showMessage('success', 'Activité ajoutée avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de l\'ajout de l\'activité');
    }
  };

  const handleUpdateActivite = async (id, blocId, updates) => {
    try {
      const { error } = await supabase
        .from('activites')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setActivites(prev => ({
        ...prev,
        [blocId]: prev[blocId].map(a => a.id === id ? { ...a, ...updates } : a)
      }));
      setEditingItem(null);
      showMessage('success', 'Activité mise à jour');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteActivite = async (id, blocId) => {
    if (!window.confirm('Supprimer cette activité et toutes ses tâches ?')) return;
    
    try {
      const { error } = await supabase.from('activites').delete().eq('id', id);
      if (error) throw error;

      setActivites(prev => ({
        ...prev,
        [blocId]: prev[blocId].filter(a => a.id !== id)
      }));
      showMessage('success', 'Activité supprimée');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de la suppression');
    }
  };

  // CRUD Tâche
  const handleAddTache = async (activiteId) => {
    try {
      const tacheToAdd = { ...newTache, activite_id: activiteId };
      const { data, error } = await supabase
        .from('taches')
        .insert([tacheToAdd])
        .select()
        .single();

      if (error) throw error;

      setTaches(prev => ({
        ...prev,
        [activiteId]: [...(prev[activiteId] || []), data]
      }));
      setShowAddTache(null);
      setNewTache({ code: '', titre: '', description: '', ordre: 0 });
      showMessage('success', 'Tâche ajoutée avec succès');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de l\'ajout de la tâche');
    }
  };

  const handleUpdateTache = async (id, activiteId, updates) => {
    try {
      const { error } = await supabase
        .from('taches')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      setTaches(prev => ({
        ...prev,
        [activiteId]: prev[activiteId].map(t => t.id === id ? { ...t, ...updates } : t)
      }));
      setEditingItem(null);
      showMessage('success', 'Tâche mise à jour');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTache = async (id, activiteId) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    
    try {
      const { error } = await supabase.from('taches').delete().eq('id', id);
      if (error) throw error;

      setTaches(prev => ({
        ...prev,
        [activiteId]: prev[activiteId].filter(t => t.id !== id)
      }));
      showMessage('success', 'Tâche supprimée');
    } catch (error) {
      console.error('Erreur:', error);
      showMessage('error', 'Erreur lors de la suppression');
    }
  };

  // Filtrage
  const filteredPoles = poles.filter(pole => {
    const searchLower = searchTerm.toLowerCase();
    return pole.titre.toLowerCase().includes(searchLower) ||
           pole.code.toLowerCase().includes(searchLower);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <FolderTree className="w-10 h-10 text-blue-400" />
            Référentiel de Compétences
          </h1>
          <p className="text-gray-400">Gestion hiérarchique : Pôles → Blocs → Activités → Tâches</p>
        </div>

        {/* Message de notification */}
        <AnimatePresence>
          {message.text && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                message.type === 'success' 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/50' 
                  : 'bg-red-500/20 text-red-400 border border-red-500/50'
              }`}
            >
              {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
              <span>{message.text}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Filtres et actions */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 mb-6 border border-slate-700">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Filtre formation */}
            <select
              value={selectedFormation}
              onChange={(e) => setSelectedFormation(e.target.value)}
              className="flex-1 bg-slate-700 border border-slate-600 rounded-lg py-3 px-4 text-white focus:border-blue-500 focus:outline-none"
            >
              {typesFormation.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>

            {/* Recherche */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg py-3 pl-12 pr-4 text-white placeholder-gray-400 focus:border-blue-500 focus:outline-none"
              />
            </div>

            {/* Bouton ajout pôle */}
            <button
              onClick={() => setShowAddPole(true)}
              className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg flex items-center gap-2 text-white font-medium transition-colors whitespace-nowrap"
            >
              <Plus className="w-5 h-5" />
              Nouveau Pôle
            </button>
          </div>
        </div>

        {/* Formulaire ajout pôle */}
        <AnimatePresence>
          {showAddPole && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-slate-800 rounded-xl p-6 mb-6 border border-blue-500/50"
            >
              <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-400" />
                Nouveau Pôle
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Code (ex: POLE_1)"
                  value={newPole.code}
                  onChange={(e) => setNewPole({ ...newPole, code: e.target.value })}
                  className="bg-slate-700 border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400"
                />
                <select
                  value={newPole.type_formation}
                  onChange={(e) => setNewPole({ ...newPole, type_formation: e.target.value })}
                  className="bg-slate-700 border border-slate-600 rounded-lg py-2 px-4 text-white"
                >
                  {typesFormation.filter(t => t.value !== 'all').map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              <input
                type="text"
                placeholder="Titre du pôle"
                value={newPole.titre}
                onChange={(e) => setNewPole({ ...newPole, titre: e.target.value })}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 mb-4"
              />
              <textarea
                placeholder="Description (optionnelle)"
                value={newPole.description}
                onChange={(e) => setNewPole({ ...newPole, description: e.target.value })}
                rows={3}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg py-2 px-4 text-white placeholder-gray-400 mb-4"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAddPole(false)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 px-4 py-2 rounded-lg transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleAddPole}
                  disabled={!newPole.code || !newPole.titre}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  Enregistrer
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Liste des pôles */}
        <div className="space-y-4">
          {filteredPoles.length === 0 ? (
            <div className="bg-slate-800/50 rounded-xl p-12 text-center border border-slate-700">
              <FolderTree className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg">Aucun pôle trouvé</p>
              <p className="text-gray-500 text-sm mt-2">Commencez par créer un nouveau pôle</p>
            </div>
          ) : (
            filteredPoles.map((pole, poleIndex) => (
              <motion.div
                key={pole.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-slate-800/70 backdrop-blur-sm rounded-xl border border-slate-700 overflow-hidden"
              >
                {/* Header Pôle */}
                <div className="p-4 flex items-center justify-between bg-gradient-to-r from-blue-600/20 to-transparent border-l-4 border-blue-500">
                  <div className="flex items-center gap-3 flex-1">
                    <button
                      onClick={() => toggleExpand('pole', pole.id)}
                      className="p-1 hover:bg-slate-700 rounded transition-colors"
                    >
                      {expandedItems[`pole_${pole.id}`] ? (
                        <ChevronDown className="w-5 h-5 text-blue-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-blue-400" />
                      )}
                    </button>
                    <FolderTree className="w-6 h-6 text-blue-400" />
                    {editingItem === `pole_${pole.id}` ? (
                      <div className="flex-1 flex gap-2">
                        <input
                          type="text"
                          defaultValue={pole.code}
                          onBlur={(e) => {
                            if (e.target.value !== pole.code) {
                              handleUpdatePole(pole.id, { code: e.target.value });
                            }
                          }}
                          className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white text-sm"
                        />
                        <input
                          type="text"
                          defaultValue={pole.titre}
                          onBlur={(e) => {
                            if (e.target.value !== pole.titre) {
                              handleUpdatePole(pole.id, { titre: e.target.value });
                            }
                          }}
                          className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-white"
                        />
                      </div>
                    ) : (
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="text-blue-400 font-mono text-sm font-semibold">{pole.code}</span>
                          <span className="text-white font-semibold">{pole.titre}</span>
                          <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded">
                            {typesFormation.find(t => t.value === pole.type_formation)?.label}
                          </span>
                        </div>
                        {pole.description && (
                          <p className="text-gray-400 text-sm mt-1">{pole.description}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setEditingItem(editingItem === `pole_${pole.id}` ? null : `pole_${pole.id}`)}
                      className="p-2 text-blue-400 hover:bg-slate-700 rounded transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeletePole(pole.id)}
                      className="p-2 text-red-400 hover:bg-slate-700 rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Blocs */}
                <AnimatePresence>
                  {expandedItems[`pole_${pole.id}`] && (
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: 'auto' }}
                      exit={{ height: 0 }}
                      className="border-t border-slate-700"
                    >
                      <div className="p-4 space-y-3">
                        {/* Bouton ajout bloc */}
                        {showAddBloc === pole.id ? (
                          <div className="bg-slate-700/50 rounded-lg p-4 space-y-3">
                            <input
                              type="text"
                              placeholder="Code bloc (ex: UP1)"
                              value={newBloc.code}
                              onChange={(e) => setNewBloc({ ...newBloc, code: e.target.value })}
                              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white placeholder-gray-400"
                            />
                            <input
                              type="text"
                              placeholder="Titre du bloc"
                              value={newBloc.titre}
                              onChange={(e) => setNewBloc({ ...newBloc, titre: e.target.value })}
                              className="w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white placeholder-gray-400"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => setShowAddBloc(null)}
                                className="flex-1 bg-slate-600 hover:bg-slate-500 px-3 py-2 rounded text-sm"
                              >
                                Annuler
                              </button>
                              <button
                                onClick={() => handleAddBloc(pole.id)}
                                disabled={!newBloc.code || !newBloc.titre}
                                className="flex-1 bg-green-600 hover:bg-green-700 px-3 py-2 rounded text-sm disabled:opacity-50"
                              >
                                <Save className="w-4 h-4 inline mr-1" />
                                Enregistrer
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowAddBloc(pole.id)}
                            className="w-full bg-slate-700/30 hover:bg-slate-700 border border-dashed border-slate-600 rounded-lg py-2 flex items-center justify-center gap-2 text-gray-400 hover:text-white transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                            Ajouter un bloc
                          </button>
                        )}

                        {/* Liste des blocs */}
                        {(blocs[pole.id] || []).map((bloc) => (
                          <div key={bloc.id} className="bg-slate-700/50 rounded-lg">
                            <div className="p-3 flex items-center justify-between border-l-4 border-green-500">
                              <div className="flex items-center gap-2 flex-1">
                                <button
                                  onClick={() => toggleExpand('bloc', bloc.id)}
                                  className="p-1 hover:bg-slate-600 rounded"
                                >
                                  {expandedItems[`bloc_${bloc.id}`] ? (
                                    <ChevronDown className="w-4 h-4 text-green-400" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-green-400" />
                                  )}
                                </button>
                                <Box className="w-5 h-5 text-green-400" />
                                {editingItem === `bloc_${bloc.id}` ? (
                                  <div className="flex-1 flex gap-2">
                                    <input
                                      type="text"
                                      defaultValue={bloc.code}
                                      onBlur={(e) => {
                                        if (e.target.value !== bloc.code) {
                                          handleUpdateBloc(bloc.id, pole.id, { code: e.target.value });
                                        }
                                      }}
                                      className="bg-slate-600 border border-slate-500 rounded px-2 py-1 text-white text-sm"
                                    />
                                    <input
                                      type="text"
                                      defaultValue={bloc.titre}
                                      onBlur={(e) => {
                                        if (e.target.value !== bloc.titre) {
                                          handleUpdateBloc(bloc.id, pole.id, { titre: e.target.value });
                                        }
                                      }}
                                      className="flex-1 bg-slate-600 border border-slate-500 rounded px-2 py-1 text-white text-sm"
                                    />
                                  </div>
                                ) : (
                                  <div className="flex-1">
                                    <span className="text-green-400 font-mono text-sm font-semibold mr-2">{bloc.code}</span>
                                    <span className="text-white text-sm">{bloc.titre}</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => setEditingItem(editingItem === `bloc_${bloc.id}` ? null : `bloc_${bloc.id}`)}
                                  className="p-1.5 text-green-400 hover:bg-slate-600 rounded"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteBloc(bloc.id, pole.id)}
                                  className="p-1.5 text-red-400 hover:bg-slate-600 rounded"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>

                            {/* Activités */}
                            <AnimatePresence>
                              {expandedItems[`bloc_${bloc.id}`] && (
                                <motion.div
                                  initial={{ height: 0 }}
                                  animate={{ height: 'auto' }}
                                  exit={{ height: 0 }}
                                  className="p-3 space-y-2 border-t border-slate-600"
                                >
                                  {/* Bouton ajout activité */}
                                  {showAddActivite === bloc.id ? (
                                    <div className="bg-slate-600/50 rounded p-3 space-y-2">
                                      <input
                                        type="text"
                                        placeholder="Code (ex: A1.1)"
                                        value={newActivite.code}
                                        onChange={(e) => setNewActivite({ ...newActivite, code: e.target.value })}
                                        className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-sm placeholder-gray-400"
                                      />
                                      <input
                                        type="text"
                                        placeholder="Titre de l'activité"
                                        value={newActivite.titre}
                                        onChange={(e) => setNewActivite({ ...newActivite, titre: e.target.value })}
                                        className="w-full bg-slate-600 border border-slate-500 rounded px-2 py-1.5 text-white text-sm placeholder-gray-400"
                                      />
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() => setShowAddActivite(null)}
                                          className="flex-1 bg-slate-500 hover:bg-slate-400 px-2 py-1.5 rounded text-xs"
                                        >
                                          Annuler
                                        </button>
                                        <button
                                          onClick={() => handleAddActivite(bloc.id)}
                                          disabled={!newActivite.code || !newActivite.titre}
                                          className="flex-1 bg-yellow-600 hover:bg-yellow-700 px-2 py-1.5 rounded text-xs disabled:opacity-50"
                                        >
                                          Enregistrer
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setShowAddActivite(bloc.id)}
                                      className="w-full bg-slate-600/30 hover:bg-slate-600 border border-dashed border-slate-500 rounded py-2 flex items-center justify-center gap-2 text-gray-400 hover:text-white text-sm"
                                    >
                                      <Plus className="w-3.5 h-3.5" />
                                      Ajouter une activité
                                    </button>
                                  )}

                                  {/* Liste des activités */}
                                  {(activites[bloc.id] || []).map((activite) => (
                                    <div key={activite.id} className="bg-slate-600/50 rounded">
                                      <div className="p-2 flex items-center justify-between border-l-4 border-yellow-500">
                                        <div className="flex items-center gap-2 flex-1">
                                          <button
                                            onClick={() => toggleExpand('activite', activite.id)}
                                            className="p-1 hover:bg-slate-500 rounded"
                                          >
                                            {expandedItems[`activite_${activite.id}`] ? (
                                              <ChevronDown className="w-3.5 h-3.5 text-yellow-400" />
                                            ) : (
                                              <ChevronRight className="w-3.5 h-3.5 text-yellow-400" />
                                            )}
                                          </button>
                                          <Target className="w-4 h-4 text-yellow-400" />
                                          {editingItem === `activite_${activite.id}` ? (
                                            <div className="flex-1 flex gap-2">
                                              <input
                                                type="text"
                                                defaultValue={activite.code}
                                                onBlur={(e) => {
                                                  if (e.target.value !== activite.code) {
                                                    handleUpdateActivite(activite.id, bloc.id, { code: e.target.value });
                                                  }
                                                }}
                                                className="bg-slate-500 border border-slate-400 rounded px-2 py-1 text-white text-xs"
                                              />
                                              <input
                                                type="text"
                                                defaultValue={activite.titre}
                                                onBlur={(e) => {
                                                  if (e.target.value !== activite.titre) {
                                                    handleUpdateActivite(activite.id, bloc.id, { titre: e.target.value });
                                                  }
                                                }}
                                                className="flex-1 bg-slate-500 border border-slate-400 rounded px-2 py-1 text-white text-xs"
                                              />
                                            </div>
                                          ) : (
                                            <div className="flex-1">
                                              <span className="text-yellow-400 font-mono text-xs font-semibold mr-2">{activite.code}</span>
                                              <span className="text-white text-xs">{activite.titre}</span>
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <button
                                            onClick={() => setEditingItem(editingItem === `activite_${activite.id}` ? null : `activite_${activite.id}`)}
                                            className="p-1 text-yellow-400 hover:bg-slate-500 rounded"
                                          >
                                            <Edit2 className="w-3 h-3" />
                                          </button>
                                          <button
                                            onClick={() => handleDeleteActivite(activite.id, bloc.id)}
                                            className="p-1 text-red-400 hover:bg-slate-500 rounded"
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </button>
                                        </div>
                                      </div>

                                      {/* Tâches */}
                                      <AnimatePresence>
                                        {expandedItems[`activite_${activite.id}`] && (
                                          <motion.div
                                            initial={{ height: 0 }}
                                            animate={{ height: 'auto' }}
                                            exit={{ height: 0 }}
                                            className="p-2 space-y-1.5 border-t border-slate-500"
                                          >
                                            {/* Bouton ajout tâche */}
                                            {showAddTache === activite.id ? (
                                              <div className="bg-slate-500/50 rounded p-2 space-y-2">
                                                <input
                                                  type="text"
                                                  placeholder="Code (ex: T1.1.1)"
                                                  value={newTache.code}
                                                  onChange={(e) => setNewTache({ ...newTache, code: e.target.value })}
                                                  className="w-full bg-slate-500 border border-slate-400 rounded px-2 py-1 text-white text-xs placeholder-gray-400"
                                                />
                                                <input
                                                  type="text"
                                                  placeholder="Titre de la tâche"
                                                  value={newTache.titre}
                                                  onChange={(e) => setNewTache({ ...newTache, titre: e.target.value })}
                                                  className="w-full bg-slate-500 border border-slate-400 rounded px-2 py-1 text-white text-xs placeholder-gray-400"
                                                />
                                                <div className="flex gap-2">
                                                  <button
                                                    onClick={() => setShowAddTache(null)}
                                                    className="flex-1 bg-slate-400 hover:bg-slate-300 px-2 py-1 rounded text-xs"
                                                  >
                                                    Annuler
                                                  </button>
                                                  <button
                                                    onClick={() => handleAddTache(activite.id)}
                                                    disabled={!newTache.code || !newTache.titre}
                                                    className="flex-1 bg-purple-600 hover:bg-purple-700 px-2 py-1 rounded text-xs disabled:opacity-50"
                                                  >
                                                    Enregistrer
                                                  </button>
                                                </div>
                                              </div>
                                            ) : (
                                              <button
                                                onClick={() => setShowAddTache(activite.id)}
                                                className="w-full bg-slate-500/30 hover:bg-slate-500 border border-dashed border-slate-400 rounded py-1.5 flex items-center justify-center gap-2 text-gray-400 hover:text-white text-xs"
                                              >
                                                <Plus className="w-3 h-3" />
                                                Ajouter une tâche
                                              </button>
                                            )}

                                            {/* Liste des tâches */}
                                            {(taches[activite.id] || []).map((tache) => (
                                              <div key={tache.id} className="bg-slate-500/50 rounded p-2 flex items-center justify-between border-l-2 border-purple-500">
                                                {editingItem === `tache_${tache.id}` ? (
                                                  <div className="flex-1 flex gap-2">
                                                    <input
                                                      type="text"
                                                      defaultValue={tache.code}
                                                      onBlur={(e) => {
                                                        if (e.target.value !== tache.code) {
                                                          handleUpdateTache(tache.id, activite.id, { code: e.target.value });
                                                        }
                                                      }}
                                                      className="bg-slate-400 border border-slate-300 rounded px-2 py-0.5 text-white text-xs"
                                                    />
                                                    <input
                                                      type="text"
                                                      defaultValue={tache.titre}
                                                      onBlur={(e) => {
                                                        if (e.target.value !== tache.titre) {
                                                          handleUpdateTache(tache.id, activite.id, { titre: e.target.value });
                                                        }
                                                      }}
                                                      className="flex-1 bg-slate-400 border border-slate-300 rounded px-2 py-0.5 text-white text-xs"
                                                    />
                                                  </div>
                                                ) : (
                                                  <div className="flex items-center gap-2 flex-1">
                                                    <CheckSquare className="w-3.5 h-3.5 text-purple-400" />
                                                    <span className="text-purple-400 font-mono text-xs font-semibold">{tache.code}</span>
                                                    <span className="text-white text-xs">{tache.titre}</span>
                                                  </div>
                                                )}
                                                <div className="flex items-center gap-1">
                                                  <button
                                                    onClick={() => setEditingItem(editingItem === `tache_${tache.id}` ? null : `tache_${tache.id}`)}
                                                    className="p-1 text-purple-400 hover:bg-slate-400 rounded"
                                                  >
                                                    <Edit2 className="w-2.5 h-2.5" />
                                                  </button>
                                                  <button
                                                    onClick={() => handleDeleteTache(tache.id, activite.id)}
                                                    className="p-1 text-red-400 hover:bg-slate-400 rounded"
                                                  >
                                                    <Trash2 className="w-2.5 h-2.5" />
                                                  </button>
                                                </div>
                                              </div>
                                            ))}
                                          </motion.div>
                                        )}
                                      </AnimatePresence>
                                    </div>
                                  ))}
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CompetencesManagerV2;